import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '../services/supabaseClient';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
    const [session, setSession] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [memberStatus, setMemberStatus] = useState(null);
    // loading hanya true saat aplikasi pertama kali dimuat. Event auth sesudahnya
    // (login, pendaftaran, refresh token) memuat ulang profil tanpa melepas aplikasi dari layar.
    const [loading, setLoading] = useState(true);
    // Penanda urutan permintaan: hasil permintaan lama tidak boleh menimpa yang lebih baru.
    const requestSeq = useRef(0);

    // Mengekstrak Role dan Status pendaftaran
    const fetchUserProfile = useCallback(async (currentUserSession) => {
        const seq = ++requestSeq.current;

        if (!currentUserSession) {
            setSession(null);
            setUserRole(null);
            setMemberStatus(null);
            setLoading(false);
            return;
        }

        const userId = currentUserSession.user.id;
        let resolved = false;
        let nextRole = null;
        let nextStatus = null;

        try {
            // Cek apakah user adalah Admin menggunakan fungsi SECURITY DEFINER via RPC
            const { data: adminRole } = await supabase.rpc('get_admin_role');

            if (adminRole) {
                nextRole = adminRole;
            } else {
                // Jika bukan admin, cari data registrasinya (boleh belum ada, misalnya saat pendaftaran berjalan)
                const { data: memberData } = await supabase
                    .from('registrations')
                    .select('status')
                    .eq('user_id', userId)
                    .maybeSingle();

                nextRole = 'member';
                nextStatus = memberData ? memberData.status : null;
            }
            resolved = true;
        } catch (error) {
            console.error("Error fetching user profile:", error);
        }

        // Ada permintaan yang lebih baru, biarkan permintaan itu yang menetapkan state
        if (seq !== requestSeq.current) return;

        // Kalau gagal dimuat, peran dan status sebelumnya dipertahankan
        if (resolved) {
            setUserRole(nextRole);
            setMemberStatus(nextStatus);
        }
        setSession(currentUserSession);
        setLoading(false);
    }, []);

    // Dipanggil setelah pendaftaran selesai agar layar langsung memakai status terbaru
    const refreshMemberStatus = useCallback(async () => {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        await fetchUserProfile(currentSession);
    }, [fetchUserProfile]);

    useEffect(() => {
        // Ambil sesi awal saat aplikasi dimuat
        supabase.auth.getSession().then(({ data: { session } }) => {
            fetchUserProfile(session);
        });

        // Dengarkan perubahan login/logout
        const { data: authListener } = supabase.auth.onAuthStateChange(
            (_event, session) => {
                fetchUserProfile(session);
            }
        );

        return () => {
            authListener.subscription.unsubscribe();
        };
    }, [fetchUserProfile]);

    return (
        <AuthContext.Provider value={{ session, userRole, memberStatus, loading, refreshMemberStatus }}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    return useContext(AuthContext);
};
